import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import WishlistButton from "../Wishlist/WishlistButton";
import { toast } from "react-toastify";
import { Helmet } from 'react-helmet-async';
import { useProductTranslation } from "../../i18n/useProductTranslation";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function formatMoney(value, currency = "EUR") {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency,
  }).format(num);
}

function formatMeasure(value, unit = "") {
  if (value === null || value === undefined || value === "") return "—";
  return `${value} ${unit}`.trim();
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem, openCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedIndex, setRelatedIndex] = useState(0);

  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const [variantTab, setVariantTab] = useState("base");
  const [selectedType, setSelectedType] = useState("base");
  const [selectedHeight, setSelectedHeight] = useState("");
  const [selectedWidth, setSelectedWidth] = useState("");
  const [selectedDepth, setSelectedDepth] = useState("");
  const [selectedLevels, setSelectedLevels] = useState("");
  const [selectedLoad, setSelectedLoad] = useState("");

  const { t, getTranslatedProduct, getTranslatedVariant, getVariantTypeLabel, getTranslatedFeatures, getRatingLabel } = useProductTranslation();

  const translatedProduct = useMemo(() => getTranslatedProduct(product), [getTranslatedProduct, product]);
  const translatedSelectedVariant = useMemo(() => selectedVariant ? getTranslatedVariant(selectedVariant, product?.id) : null, [getTranslatedVariant, selectedVariant, product?.id]);
  const translatedRelatedProducts = useMemo(() => relatedProducts.map(p => getTranslatedProduct(p)), [getTranslatedProduct, relatedProducts]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchProduct = async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_BASE_URL}/api/v1/products/${id}`, {
          headers: authHeaders,
        });

        const data = res.data.data;

        setProduct(data);
        setRelatedProducts(res.data.related?.data || []);
        setMainImageIndex(0);
        setSelectedVariant(null);
        setVariants([]);

        const defaultType =
          data?.variant_counts?.base > 0
            ? "base"
            : data?.variant_counts?.next > 0
              ? "next"
              : "base";

        setVariantTab(defaultType);
        setSelectedType(defaultType);
        setSelectedHeight("");
        setSelectedWidth("");
        setSelectedDepth("");
        setSelectedLevels("");
        setSelectedLoad("");
      } catch (error) {
        console.error("Erro ao buscar produto:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCanReview = async () => {
      if (!token) return;

      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/v1/products/${id}/can-review`,
          { headers: authHeaders }
        );
        setCanReview(res.data.canReview);
      } catch {
        setCanReview(false);
      }
    };

    Promise.all([fetchProduct(), fetchCanReview()]);
  }, [id]);

  const canSearchVariants =
    selectedHeight !== "" || selectedWidth !== "" || selectedDepth !== "";

  useEffect(() => {
    if (!product?.has_variants) return;

    if (!canSearchVariants) {
      setVariants([]);
      setSelectedVariant(null);
      return;
    }

    const token = localStorage.getItem("token");
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchVariants = async () => {
      try {
        setVariantsLoading(true);

        const params = {
          type: selectedType,
          height_cm: selectedHeight,
          width_cm: selectedWidth,
          depth_cm: selectedDepth,
        };

        if (selectedLevels !== "") params.levels = selectedLevels;
        if (selectedLoad !== "") params.load_kg = selectedLoad;

        const res = await axios.get(
          `${API_BASE_URL}/api/v1/products/${id}/variants`,
          {
            headers: authHeaders,
            params,
          }
        );

        const list = res.data.data || [];
        setVariants(list);

        if (list.length === 1) {
          setSelectedVariant(list[0]);
        } else {
          setSelectedVariant((prev) =>
            prev && list.some((v) => v.id === prev.id) ? prev : null
          );
        }
      } catch (error) {
        console.error("Erro ao buscar variantes:", error);
        setVariants([]);
        setSelectedVariant(null);
      } finally {
        setVariantsLoading(false);
      }
    };

    fetchVariants();
  }, [
    id,
    product?.has_variants,
    selectedType,
    selectedHeight,
    selectedWidth,
    selectedDepth,
    selectedLevels,
    selectedLoad,
    canSearchVariants,
  ]);

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen py-8 px-4 sm:px-6 animate-pulse">
        <div className="max-w-6xl mx-auto mb-5 flex gap-2">
          <div className="h-4 w-10 bg-gray-200 rounded" />
          <div className="h-4 w-2 bg-gray-200 rounded" />
          <div className="h-4 w-12 bg-gray-200 rounded" />
          <div className="h-4 w-2 bg-gray-200 rounded" />
          <div className="h-4 w-32 bg-gray-200 rounded" />
        </div>

        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-[55%] p-6 flex flex-col gap-4 border-b md:border-b-0 md:border-r border-gray-100">
              <div className="aspect-square bg-gray-200 rounded-xl" />
              <div className="flex gap-2 justify-center">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-16 h-16 bg-gray-200 rounded-lg" />
                ))}
              </div>
            </div>

            <div className="md:w-[45%] p-6 flex flex-col gap-5">
              <div className="space-y-2">
                <div className="h-4 w-20 bg-gray-200 rounded-full" />
                <div className="h-8 w-3/4 bg-gray-200 rounded-lg" />
              </div>
              <div className="h-9 w-32 bg-gray-200 rounded-lg" />
              <div className="h-40 bg-gray-200 rounded-xl" />
              <div className="h-28 bg-gray-200 rounded-xl" />
              <div className="h-12 bg-gray-200 rounded-xl mt-auto" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-xl">{t("Produto não encontrado")}</p>
      </div>
    );
  }

  const hasVariants = !!translatedProduct?.has_variants;

  const images =
    translatedProduct.images?.length > 0
      ? translatedProduct.images.map((img) => img.image_url)
      : translatedProduct.image_url
        ? [translatedProduct.image_url]
        : [];

  const safeImages =
    images.length > 0 ? images : ["/placeholder-product.png"];

  const prevImage = () =>
    setMainImageIndex((prev) =>
      prev === 0 ? safeImages.length - 1 : prev - 1
    );

  const nextImage = () =>
    setMainImageIndex((prev) =>
      prev === safeImages.length - 1 ? 0 : prev + 1
    );

  const averageRating =
    product.reviews && product.reviews.length > 0
      ? (
        product.reviews.reduce((acc, r) => acc + r.rating, 0) /
        product.reviews.length
      ).toFixed(1)
      : 0;

  const sortedReviews = product.reviews
    ? [...product.reviews].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
    : [];

  const visibleReviews = showAllReviews
    ? sortedReviews
    : sortedReviews.slice(0, 5);

  const visibleVariantList = variants;

  const handleAddToCart = () => {
    if (hasVariants && !selectedVariant) return;
    if (!selectedVariant) return;

    const cartItem = {
      id: selectedVariant.id,
      product_id: product.id,
      name: product.name,
      price: selectedVariant.price,
      variant_id: selectedVariant.id,
      variant_label: `${selectedVariant.height_cm ?? "—"} x ${selectedVariant.width_cm ?? "—"
        } x ${selectedVariant.depth_cm ?? "—"} cm`,
      variant_type: selectedVariant.is_next ? t("Seguinte") : t("Base"),
      image_url:
        product.images?.length > 0
          ? product.images[0].image_url
          : product.image_url || null,
      quantity: 1,
    };

    addItem(cartItem);
    openCart();
  };

  const ITEMS_PER_PAGE = 4;
  const totalPages = Math.ceil(relatedProducts.length / ITEMS_PER_PAGE);

  const visibleRelated = relatedProducts.slice(
    relatedIndex * ITEMS_PER_PAGE,
    relatedIndex * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  const nextRelated = () =>
    setRelatedIndex((prev) => (prev + 1 >= totalPages ? 0 : prev + 1));

  const prevRelated = () =>
    setRelatedIndex((prev) => (prev - 1 < 0 ? totalPages - 1 : prev - 1));

  const submitReview = async (e) => {
    e.preventDefault();

    try {
      setSubmittingReview(true);

      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${API_BASE_URL}/api/v1/products/${translatedProduct.id}/review`,
        { rating: reviewRating, comment: reviewComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const createdReview = res.data.data;

      setProduct((prev) => ({
        ...prev,
        reviews: [createdReview, ...(prev.reviews || [])],
      }));

      setReviewComment("");
      setReviewRating(5);
      setCanReview(false);
      setShowReviewModal(false);

      toast.success(t("Avaliação enviada com sucesso!"));
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("Erro ao enviar avaliação.")
      );
    } finally {
      setSubmittingReview(false);
    }
  };

  const rawFeatures = translatedProduct.features;
  const featuresArray = Array.isArray(rawFeatures)
    ? rawFeatures
    : (typeof rawFeatures === "string" ? rawFeatures.split("\\n").map((item) => item.trim()).filter(Boolean) : []);
  
  const features = getTranslatedFeatures(translatedProduct, featuresArray);

  return (
    <div className="bg-gray-100 min-h-screen pt-24 pb-8 px-4 sm:px-6">

      <Helmet>
        <title>{translatedProduct.name} | Racksforall</title>
        <meta name="description" content={translatedProduct.description?.slice(0, 160) ?? ''} />
        <link rel="canonical" href={`https://racksforall.pt/products/${translatedProduct.id}`} />
        <meta property="og:title" content={translatedProduct.name} />
        <meta property="og:description" content={translatedProduct.description?.slice(0, 160) ?? ''} />
        <meta property="og:image" content={safeImages[0]} />
        <meta property="og:url" content={`https://racksforall.pt/products/${translatedProduct.id}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": translatedProduct.name,
            "description": translatedProduct.description ?? '',
            "sku": translatedProduct.sku ?? '',
            "image": safeImages,
            "brand": { "@type": "Brand", "name": "Racksforall" },
            "aggregateRating": translatedProduct.reviews?.length > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": averageRating,
              "reviewCount": translatedProduct.reviews.length
            } : undefined,
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "EUR",
              "lowPrice": translatedProduct.display_price ?? 0,
              "availability": translatedProduct.variants?.some(v => v.stock > 0)
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock"
            }
          })}
        </script>
      </Helmet>
      <div className="max-w-6xl mx-auto mb-5 text-sm text-gray-500">
        <Link to="/" className="hover:text-indigo-600 transition">
          {t("Início")}
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to="/products" className="hover:text-indigo-600 transition">
          {t("Loja")}
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{translatedProduct.name}</span>
      </div>

      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">

          <div className="md:w-[55%] p-6 flex flex-col gap-4 border-b md:border-b-0 md:border-r border-gray-100">

            <div className="relative aspect-square">
              {/* Sombra + arredondamento na imagem */}
              <div
                className="w-full h-full overflow-hidden rounded-xl shadow-md"
                onMouseEnter={() => { }}
                onMouseLeave={() => { }}
              >
                <img
                  src={safeImages[mainImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-all duration-500"
                />
              </div>

              {safeImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full w-9 h-9 flex items-center justify-center shadow hover:bg-white transition text-gray-600 z-10"
                  >
                    &#10094;
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full w-9 h-9 flex items-center justify-center shadow hover:bg-white transition text-gray-600 z-10"
                  >
                    &#10095;
                  </button>
                </>
              )}
            </div>

            {/* Miniaturas */}
            <div className="flex gap-2 justify-center flex-wrap">
              {safeImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setMainImageIndex(index)}
                  className={`w-16 h-16 rounded-lg border-2 overflow-hidden transition flex-shrink-0 ${mainImageIndex === index
                    ? "border-indigo-500 shadow-md"
                    : "border-gray-200 hover:border-indigo-300"
                    }`}
                >
                  <img
                    src={img}
                    alt={`${t("Miniatura")} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Coluna de detalhes */}
          <div className="md:w-[45%] flex flex-col p-6 gap-5">
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                {translatedProduct.sku && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-mono">
                    REF: {translatedProduct.sku}
                  </span>
                )}

                {translatedProduct.categories?.map((cat) => (
                  <span
                    key={cat.id}
                    className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-medium"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                {translatedProduct.name}
              </h1>
            </div>

            <div>
              <p className="text-3xl font-extrabold text-indigo-600">
                {translatedSelectedVariant
                  ? formatMoney(translatedSelectedVariant.price, translatedSelectedVariant.currency || "EUR")
                  : translatedProduct.display_price
                    ? formatMoney(translatedProduct.display_price, "EUR")
                    : t("Sob consulta")}
              </p>

              {(translatedSelectedVariant || translatedProduct.display_price) && (
                <p className="mt-1 text-xs text-gray-500">
                  {t("IVA incluído")}
                </p>
              )}
            </div>

            {hasVariants && (
              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <div className="border-b bg-gray-50 px-4 py-3">
                  <p className="text-sm font-semibold text-gray-800">
                    {t("Escolher variante")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Filtra por dimensões, níveis e carga para encontrar a variante certa.")}
                  </p>
                </div>

                <div className="flex border-b bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      setVariantTab("base");
                      setSelectedType("base");
                      setSelectedVariant(null);
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition ${variantTab === "base"
                      ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
                      : "text-gray-500 hover:text-gray-800"
                      }`}
                  >
                    {t("Base")}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVariantTab("next");
                      setSelectedType("next");
                      setSelectedVariant(null);
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition ${variantTab === "next"
                      ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
                      : "text-gray-500 hover:text-gray-800"
                      }`}
                  >
                    {t("Seguinte")}
                  </button>
                </div>

                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b bg-white">
                  <select
                    value={selectedHeight}
                    onChange={(e) => {
                      setSelectedHeight(e.target.value);
                      setSelectedVariant(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">{t("Altura")}</option>
                    {(translatedProduct.variant_filters?.height_cm || []).map((value) => (
                      <option key={value} value={value}>
                        {value} cm
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedWidth}
                    onChange={(e) => {
                      setSelectedWidth(e.target.value);
                      setSelectedVariant(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">{t("Largura")}</option>
                    {(translatedProduct.variant_filters?.width_cm || []).map((value) => (
                      <option key={value} value={value}>
                        {value} cm
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedDepth}
                    onChange={(e) => {
                      setSelectedDepth(e.target.value);
                      setSelectedVariant(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">{t("Profundidade")}</option>
                    {(translatedProduct.variant_filters?.depth_cm || []).map((value) => (
                      <option key={value} value={value}>
                        {value} cm
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedLevels}
                    onChange={(e) => {
                      setSelectedLevels(e.target.value);
                      setSelectedVariant(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">{t("Níveis")}</option>
                    {(translatedProduct.variant_filters?.levels || []).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedLoad}
                    onChange={(e) => {
                      setSelectedLoad(e.target.value);
                      setSelectedVariant(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:col-span-2"
                  >
                    <option value="">{t("Carga")}</option>
                    {(translatedProduct.variant_filters?.load_kg || []).map((value) => (
                      <option key={value} value={value}>
                        {value} kg
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedHeight("");
                      setSelectedWidth("");
                      setSelectedDepth("");
                      setSelectedLevels("");
                      setSelectedLoad("");
                      setSelectedVariant(null);
                    }}
                    className="sm:col-span-2 border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    {t("Limpar filtros")}
                  </button>
                </div>

                <div className="max-h-80 overflow-auto">
                  {!canSearchVariants ? (
                    <div className="p-4 text-sm text-gray-400">
                      {t("Seleciona altura, largura e profundidade para ver variantes disponíveis.")}
                    </div>
                  ) : variantsLoading ? (
                    <div className="p-4 text-sm text-gray-400">
                      {t("A carregar variantes...")}
                    </div>
                  ) : visibleVariantList.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b text-left text-gray-500">
                          <th className="px-3 py-3 font-medium">{t("Dimensões")}</th>
                          <th className="px-3 py-3 font-medium">{t("Níveis")}</th>
                          <th className="px-3 py-3 font-medium">{t("Carga")}</th>
                          <th className="px-3 py-3 font-medium">{t("Peso")}</th>
                          <th className="px-3 py-3 font-medium">{t("Preço")}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {visibleVariantList.map((variant) => {
                          const isSelected = selectedVariant?.id === variant.id;

                          return (
                            <tr
                              key={variant.id}
                              onClick={() => setSelectedVariant(variant)}
                              className={`cursor-pointer border-b transition ${isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                                }`}
                            >
                              <td className="px-3 py-3 text-gray-700">
                                <div className="font-medium text-gray-900">
                                  {variant.height_cm ?? "—"} × {variant.width_cm ?? "—"} ×{" "}
                                  {variant.depth_cm ?? "—"} cm
                                </div>
                                {variant.sku && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {variant.sku}
                                  </div>
                                )}
                              </td>

                              <td className="px-3 py-3 text-gray-700">
                                {variant.levels ?? "—"}
                              </td>

                              <td className="px-3 py-3 text-gray-700">
                                {formatMeasure(variant.load_kg, "kg")}
                              </td>

                              <td className="px-3 py-3 text-gray-700">
                                {formatMeasure(variant.weight_kg, "kg")}
                              </td>

                              <td className="px-3 py-3 font-semibold text-indigo-600 whitespace-nowrap">
                                <div>
                                  {formatMoney(variant.price, variant.currency || "EUR")}
                                  <div className="text-[11px] font-normal text-gray-400">
                                    {t("IVA incl.")}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-4 text-sm text-gray-400">
                      {t("Não existem variantes para os filtros escolhidos.")}
                    </div>
                  )}
                </div>
              </div>
            )}

            {translatedSelectedVariant && (
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  {t("Variante selecionada")}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Tipo")}</span>
                    <span className="font-medium text-gray-800">
                      {getVariantTypeLabel(translatedSelectedVariant.is_next)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Altura")}</span>
                    <span className="font-medium text-gray-800">
                      {formatMeasure(translatedSelectedVariant.height_cm, "cm")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Largura")}</span>
                    <span className="font-medium text-gray-800">
                      {formatMeasure(translatedSelectedVariant.width_cm, "cm")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Profundidade")}</span>
                    <span className="font-medium text-gray-800">
                      {formatMeasure(translatedSelectedVariant.depth_cm, "cm")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Nº de níveis")}</span>
                    <span className="font-medium text-gray-800">
                      {translatedSelectedVariant.levels ?? "—"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Peso")}</span>
                    <span className="font-medium text-gray-800">
                      {formatMeasure(translatedSelectedVariant.weight_kg, "kg")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">{t("Carga máxima")}</span>
                    <span className="font-medium text-gray-800">
                      {formatMeasure(translatedSelectedVariant.load_kg, "kg")}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mt-auto pt-2">
              <button
                onClick={handleAddToCart}
                disabled={hasVariants && !selectedVariant}
                className="flex-1 h-12 px-6 bg-indigo-600 text-white font-semibold rounded-xl shadow hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {t("Adicionar ao Carrinho")}
              </button>

              <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-100 transition cursor-pointer">
                <WishlistButton
                  productId={translatedProduct.id}
                  initialFavorite={translatedProduct.is_favorite || false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de descrição e detalhes */}
      <div className="max-w-6xl mx-auto mt-8 bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex border-b mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("description")}
            className={`px-6 py-3 font-semibold transition whitespace-nowrap ${activeTab === "description"
              ? "border-b-2 border-indigo-600 text-indigo-600"
              : "text-gray-500 hover:text-gray-800"
              }`}
          >
            {t("Descrição")}
          </button>

          <button
            onClick={() => setActiveTab("details")}
            className={`px-6 py-3 font-semibold transition whitespace-nowrap ${activeTab === "details"
              ? "border-b-2 border-indigo-600 text-indigo-600"
              : "text-gray-500 hover:text-gray-800"
              }`}
          >
            {t("Detalhes do Produto")}
          </button>
        </div>

        {activeTab === "description" && (
          <div className="text-gray-700 leading-relaxed text-base sm:text-lg">
            {translatedProduct.description ? (
              <p className="whitespace-pre-line">{translatedProduct.description}</p>
            ) : (
              <p className="text-gray-400">{t("Sem descrição disponível.")}</p>
            )}
          </div>
        )}

        {activeTab === "details" && (
          <>
            {features.length > 0 ? (
              <ul className="space-y-2 mt-2">
                {features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-gray-700 text-sm sm:text-base"
                  >
                    <span className="text-indigo-500 mt-0.5">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400">{t("Sem detalhes disponíveis.")}</p>
            )}
          </>
        )}
      </div>

      {/* Produtos relacionados */}
      {translatedRelatedProducts.length > 0 && (
        <div className="max-w-6xl mx-auto mt-12 relative px-8">
          <h2 className="text-xl font-bold mb-5 text-gray-900">
            {t("Produtos Relacionados")}
          </h2>

          {totalPages > 1 && (
            <>
              <button
                onClick={prevRelated}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-lg rounded-full w-9 h-9 flex items-center justify-center hover:bg-gray-100 transition z-10 text-sm"
              >
                &#10094;
              </button>

              <button
                onClick={nextRelated}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-lg rounded-full w-9 h-9 flex items-center justify-center hover:bg-gray-100 transition z-10 text-sm"
              >
                &#10095;
              </button>
            </>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {visibleRelated.map((item) => {
              const translatedItem = getTranslatedProduct(item);
              return (
                <Link
                  key={item.id}
                  to={`/products/${item.id}`}
                  className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition group"
                >
                  {/* Imagem do produto relacionado com borda gradiente */}
                  <div
                    className="aspect-square mb-3 overflow-hidden rounded-lg p-[1px]"
                    style={{ background: "linear-gradient(135deg, #e5e7eb, #f3f4f6)" }}
                  >
                    <div className="w-full h-full overflow-hidden rounded-[7px] bg-white">
                      <img
                        src={item.image_url}
                        alt={translatedItem.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-2">
                    {translatedItem.name}
                  </h3>

                  <div>
                    <p className="text-indigo-600 font-bold text-sm">
                      {translatedItem.display_price
                        ? formatMoney(translatedItem.display_price, "EUR")
                        : t("Sob consulta")}
                    </p>

                    {translatedItem.display_price && (
                      <p className="text-[11px] text-gray-400">
                        {t("IVA incluído")}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Avaliações */}
      <div className="max-w-6xl mx-auto mt-10 bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900">{t("Avaliações")}</h2>

          {canReview && (
            <button
              onClick={() => setShowReviewModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
            >
              {t("Escrever avaliação")}
            </button>
          )}
        </div>

        {product.reviews && product.reviews.length > 0 ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl font-bold text-indigo-600">
                {averageRating}
              </span>

              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`text-2xl ${star <= Math.round(averageRating)
                      ? "text-yellow-400"
                      : "text-gray-300"
                      }`}
                  >
                    ★
                  </span>
                ))}
              </div>

              <span className="text-sm text-gray-400">
                ({product.reviews.length} {t("avaliações")})
              </span>
            </div>

            <div className="space-y-5">
              {visibleReviews.map((review) => (
                <div key={review.id} className="border-b pb-4 last:border-none">
                  <div className="flex items-center gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`${star <= review.rating
                          ? "text-yellow-400"
                          : "text-gray-300"
                          }`}
                      >
                        ★
                      </span>
                    ))}
                  </div>

                  <p className="text-gray-700 text-sm">{review.comment}</p>

                  {review.created_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {sortedReviews.length > 5 && (
              <div className="mt-5 text-center">
                <button
                  onClick={() => setShowAllReviews(!showAllReviews)}
                  className="px-5 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
                >
                  {showAllReviews
                    ? t("Ver menos avaliações")
                    : t("Ver mais avaliações")}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-400 text-sm">
            {t("Ainda não existem avaliações para este produto.")}
          </p>
        )}
      </div>

      {/* Modal de avaliação */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowReviewModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>

            <h3 className="text-lg font-bold mb-4 text-center">
              {t("Escrever Avaliação")}
            </h3>

            <form onSubmit={submitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Classificação")}
                </label>

                <select
                  value={reviewRating}
                  onChange={(e) => setReviewRating(parseInt(e.target.value, 10))}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                >
                  <option value={5}>{t("★★★★★ — Excelente")}</option>
                  <option value={4}>{t("★★★★☆ — Muito bom")}</option>
                  <option value={3}>{t("★★★☆☆ — Médio")}</option>
                  <option value={2}>{t("★★☆☆☆ — Fraco")}</option>
                  <option value={1}>{t("★☆☆☆☆ — Muito fraco")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Comentário")}
                </label>

                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  required
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-none"
                  placeholder={t("Partilha a tua experiência...")}
                />
              </div>

              <button
                type="submit"
                disabled={submittingReview}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-semibold"
              >
                {submittingReview ? t("A enviar...") : t("Enviar Avaliação")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}